import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { useNavigate, useParams } from 'react-router-dom';

import { AvatarUploadRef, InputColor, showToastMessage } from 'enable-ui';
import { useFormik } from 'formik';

import { useAuthStore, userProfileSelector } from 'modules/auth/core';
import { RoleKey } from 'modules/auth/core/enum';

import type { Trainer } from '../type';

import { getExtFileName, getInputStatus, isValidEmail, randomString, trimObject } from 'helpers';
import { UploadFile } from 'helpers/upload-file';
import { useBoolean, useString } from 'hooks';
import { apiCall, useFetchApiToState } from 'hooks/service';
import { isEmpty } from 'lodash';

import { SUCCESS_MESSAGE } from 'constants/message';

import { defaultTrainerForm } from '../constant';
import { formUpdateTrainerSchema } from '../schema';

export const useFullTimeDetailController = () => {
  const navigationTo = useNavigate();
  const userProfile = useAuthStore(userProfileSelector);
  const params = useParams();
  const { id } = params;
  const institutionId = userProfile?.institution?.id || '';
  const isView = userProfile?.role?.key === RoleKey.BIOLOGIC_ADMIN;

  const trainerRole = useFetchApiToState<any, any>({
    url: '/trainer-roles',
    method: 'GET',
  });
  const trainerDetailState = useFetchApiToState<any, any>({
    url: `/users/${id}`,
    method: 'GET',
    isLoading: true,
  });

  const levels =
    trainerRole.state?.map((elRole: any) => ({
      key: String(elRole.id),
      label: elRole.name,
    })) || [];

  const trainer: Trainer = trainerDetailState.state;

  const logoRef = useRef<AvatarUploadRef>(null);

  const logoURL = useString('');

  const isChangeAvatar = useBoolean(false);

  const [logo, setLogo] = useState<File | null>(null);

  const isRemoveLogo = useBoolean(false);

  const confirmPassword = useString('');

  useEffect(() => {
    if (!id) {
      return;
    }

    trainerRole.fetchApi();
    trainerDetailState.fetchApi();
  }, [id]);

  const formik = useFormik({
    initialValues: defaultTrainerForm,
    validationSchema: formUpdateTrainerSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);

      let avatar = logoURL.value;

      if (values.firstName.length + values.lastName.length > 80) {
        showToastMessage('error', 'Maximum of 80 characters for the name');

        return;
      }

      if (logo) {
        const ext = getExtFileName(logo.name);

        avatar = `trainer-${randomString(4)}-${Date.now()}${ext}`;

        await UploadFile(logo, avatar);
      }

      const request: any = {
        ...trimObject(values),
        level: Number(values.level),
        email: values.email,
        institutionId: institutionId || trainer?.institution?.id,
        name: `${values.firstName || ''} ${values.lastName || ''}`.trim(),
        avatar,
      };

      if (!values.password) {
        delete request.password;
      }

      if (!isChangeAvatar.value && logoURL.value) {
        delete request.avatar;
      }

      const result: Trainer = await apiCall({
        url: `/users/${id}`,
        data: request,
        method: 'PATCH',
        isLoading: true,
        showError: true,
      });

      if (result?.id) {
        await trainerDetailState.fetchApi();

        showToastMessage('success', 'Updated Trainer successfully!');
      }
    },
  });

  const { values, isSubmitting, isValid, handleSubmit, handleChange, setFieldValue, setValues } =
    formik;

  useEffect(() => {
    if (isEmpty(trainer)) {
      return;
    }

    setValues({
      email: trainer.email,
      name: trainer.name,
      gender: trainer.physicalProfile?.gender || '',
      password: '',
      phone: trainer.phone || '',
      level: String(trainer.trainerRole?.id || ''),
      firstName: trainer.firstName || trainer.name,
      lastName: trainer.lastName!,
    });

    logoURL.setValue(trainer?.physicalProfile?.avatar || '');
  }, [JSON.stringify(trainer)]);

  const onChangeLevel = (item: any) => {
    if (!item) {
      return;
    }

    setFieldValue('level', item.key);
  };

  const onChangeGender = (newGenderKey: string) => () => {
    if (isView) {
      return;
    }

    setFieldValue('gender', newGenderKey);
  };

  const handleChangePhone = (value: string | undefined) => {
    setFieldValue('phone', value);
  };

  const emailStatus: {
    helperText: string;
    color: InputColor;
  } = useMemo(() => {
    if (values?.email === trainer?.email) {
      return {
        helperText: '',
        color: 'default',
      };
    }

    return getInputStatus(
      values.email,
      SUCCESS_MESSAGE.EMAIL,
      isValidEmail(values.email.trim()).isValid ? '' : isValidEmail(values.email.trim()).message,
    );
  }, [values?.email, trainer?.email]);

  const onCancel = () => {
    setValues({
      email: trainer.email,
      name: trainer.name,
      gender: trainer.physicalProfile?.gender || '',
      password: '',
      phone: trainer.phone || '',
      level: String(trainer.trainerRole?.id || ''),
      firstName: trainer.firstName || trainer.name,
      lastName: trainer.lastName!,
    });

    confirmPassword.setValue('');

    logoURL.setValue(trainer?.physicalProfile?.avatar || '');
  };

  const disabled =
    isSubmitting ||
    !isValid ||
    !isValidPhoneNumber(values.phone) ||
    !values.firstName ||
    (isValidEmail(values.email.trim()).isValid ? false : true) ||
    (values.phone?.trim() === trainer.phone &&
      values.gender === trainer.physicalProfile?.gender &&
      values.name === trainer.name &&
      values.level === String(trainer.trainerRole?.id || '') &&
      values.password === '' &&
      values.firstName === trainer.firstName &&
      values.lastName === trainer.lastName &&
      values.email === trainer.email &&
      (!trainer.physicalProfile.avatar ? '' : trainer.physicalProfile.avatar) === logoURL.value) ||
    values.password !== confirmPassword.value;

  const goBack = () => {
    navigationTo(-1);
  };

  const onChangeAvatar = (file: File) => {
    isChangeAvatar.setValue(true);

    setLogo(file);

    logoURL.setValue(URL.createObjectURL(file));
  };

  const onDeleteLogo = () => {
    logoRef.current?.clearAvatar();
    setLogo(null);

    logoURL.setValue('');

    if (logoURL.value) {
      isRemoveLogo.setValue(true);
    }
  };

  const handleConfirmPassword = (event: ChangeEvent<HTMLInputElement>) => {
    confirmPassword.setValue(event.target.value);
  };

  return {
    ...values,
    isView,
    trainer,
    disabled,
    levels,
    handleChange,
    onChangeLevel,
    onChangeGender,
    handleSubmit,
    handleChangePhone,
    onCancel,
    goBack,
    logoRef,
    logoURL,
    onChangeAvatar,
    onDeleteLogo,
    emailStatus,
    confirmPassword,
    handleConfirmPassword,
  };
};
